/**
 * The wire sends category slugs and counts, nothing more (`CategoryPayload`),
 * and the catalogue file is content, not copy. What each category is called on
 * screen — and the one line that says what lives inside it — is presentation,
 * so it lives here with the screen that draws it.
 *
 * A slug missing from this map still renders: the fallback strips the hyphens
 * so a category added to the catalogue tomorrow degrades to readable text
 * instead of breaking the grid.
 *
 * Two names per category, on purpose. `label` is the catalogue's own name and
 * is what a screen uses when it has a whole line to spend — the prompt detail
 * header, a breadcrumb. `short` is for the places where the name sits inside
 * something else and has to survive a 360px column: the library's filter chips,
 * and the badge riding on a result row. Neither is a truncation of the other;
 * "Gancho e primeiros 3 segundos" and "Ganchos e abertura" are two ways of
 * naming the same shelf, and the short one is written to be read at a glance
 * rather than to be complete.
 */
export interface CategoryMeta {
  label: string;
  description: string;
  short: string;
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  'diagnostico-voz': {
    label: 'Diagnóstico e voz da marca',
    description: 'Tom de voz, vocabulário próprio, três pilares',
    short: 'Sua voz e seus pilares'
  },
  'pauta-ideacao': {
    label: 'Pauta e ideação',
    description: 'Banco de pautas, engenharia reversa, ângulos',
    short: 'O que postar'
  },
  'gancho-3-segundos': {
    label: 'Gancho e primeiros 3 segundos',
    description: 'Abertura por dor, por contraste, teste A/B',
    short: 'Ganchos e abertura'
  },
  'roteiro-reels': {
    label: 'Roteiro de Reels',
    description: '15 s, 30 s, 60 s, tutorial, bastidor, objeção',
    short: 'Roteiro de Reels'
  },
  'carrossel-estatico': {
    label: 'Carrossel e post estático',
    description: 'Oito slides, capa que para o scroll, oferta',
    short: 'Carrossel e post'
  },
  'stories-sequencias': {
    label: 'Stories e sequências',
    description: 'Aquecimento, caixinha, bastidor, venda',
    short: 'Stories'
  },
  'legenda-cta-microcopy': {
    label: 'Legenda, CTA e microcopy',
    description: 'A tarefa de dois minutos que acontece todo dia',
    short: 'Legendas e chamadas'
  },
  reaproveitamento: {
    label: 'Reaproveitamento',
    description: 'Um vídeo em cinco formatos, live em carrossel',
    short: 'Reaproveitar o gravado'
  },
  monetizacao: {
    label: 'Monetização do conteúdo',
    description: 'Briefing de marca, permuta, mídia kit, preço',
    short: 'Publi e dinheiro'
  }
};

export function categoryLabel(slug: string): string {
  return CATEGORY_META[slug]?.label ?? slug.replaceAll('-', ' ');
}

export function categoryDescription(slug: string): string | undefined {
  return CATEGORY_META[slug]?.description;
}

/**
 * The chip and badge name. Falls back to the long label rather than to the raw
 * slug, so a category added to the catalogue tomorrow reads as words in the
 * chip row even before anyone writes its short name.
 */
export function categoryShortLabel(slug: string): string {
  return CATEGORY_META[slug]?.short ?? categoryLabel(slug);
}
